/** @format */

import dayjs from "dayjs";
import { scheduling } from "lobbie";

import { useCallback, useMemo } from "react";
import SchedulingPractitionerCard from "src/components/shared/scheduling/cards/SchedulingPractitionerCard";
import { BROWSER_TIME_ZONE, IS_MOBILE_PHONE } from "src/constants";
import { usePatientSelfSchedulingContext } from "src/hooks/patient_scheduling/usePatientSelfSchedulingContext";
import { isEmptyObject, zeroTime } from "src/utils";

const PatientSchedulingPractitioner = () => {
    const {
        selfScheduleSettings,
        availability,
        setAvailability,
        selfSchedule,
        setSelfSchedule,
        practitioners,
        route,
        getMonth,
        setRoute,
        getNextRoute,
    } = usePatientSelfSchedulingContext();

    const handleSetSelected = useCallback(
        (newPractitioner: scheduling.IPractitionerNextAvailable | null) => {
            if (!selfSchedule) return;
            if (newPractitioner?.nextAvailableStartUnix) {
                const startDate = zeroTime(dayjs())
                    .set("month", availability.selectedMonth)
                    .set("year", availability.selectedYear);

                const endDate = startDate.set("date", startDate.daysInMonth()).add(1, "day");
                //Move the calendar forward if the selected practitioners next availability
                //Is after the current month
                if (endDate.unix() < newPractitioner.nextAvailableStartUnix) {
                    const nextAvailabieStartDate = zeroTime(
                        dayjs(newPractitioner.nextAvailableStartUnix),
                    ).set("date", 1);
                    const nextAvailableEndDate = nextAvailabieStartDate
                        .set("date", startDate.daysInMonth())
                        .add(1, "day");
                    setAvailability({
                        ...availability,
                        selectedMonth: nextAvailabieStartDate.month(),
                        selectedYear: nextAvailabieStartDate.year(),
                    });
                    getMonth(nextAvailabieStartDate.valueOf(), nextAvailableEndDate.valueOf());
                }
            }
            setSelfSchedule({
                ...selfSchedule,
                practitionerId: newPractitioner ? newPractitioner.id : null,
            });
            setRoute(newPractitioner ? getNextRoute() : route);
        },
        [
            availability,
            getMonth,
            getNextRoute,
            route,
            selfSchedule,
            setAvailability,
            setRoute,
            setSelfSchedule,
        ],
    );
    const render = useMemo(() => {
        const practitionersToRender = [];
        const incrementor = IS_MOBILE_PHONE ? 1 : 2;
        for (let i = 0; i < practitioners.length; i += incrementor) {
            const prac = practitioners[i];
            if (IS_MOBILE_PHONE) {
                practitionersToRender.push(
                    <SchedulingPractitionerCard
                        key={prac.id}
                        practitioner={prac}
                        selected={prac.id === selfSchedule?.practitionerId}
                        setSelected={handleSetSelected}
                        timeZone={BROWSER_TIME_ZONE}
                        hideSelectCircle={true}
                    />,
                );
                continue;
            }
            const nextPractitioner = practitioners.length > i ? practitioners[i + 1] : null;
            practitionersToRender.push(
                <div key={prac.id} className="row">
                    <div className="col-6">
                        <SchedulingPractitionerCard
                            practitioner={prac}
                            selected={prac.id === selfSchedule?.practitionerId}
                            setSelected={handleSetSelected}
                            timeZone={BROWSER_TIME_ZONE}
                            // timeZone={location?.timeZone}
                            hideSelectCircle={true}
                        />
                    </div>
                    {nextPractitioner && (
                        <div className="col-6">
                            <SchedulingPractitionerCard
                                practitioner={nextPractitioner}
                                selected={nextPractitioner.id === selfSchedule?.practitionerId}
                                setSelected={handleSetSelected}
                                timeZone={BROWSER_TIME_ZONE}
                                // timeZone={location?.timeZone}
                                hideSelectCircle={true}
                            />
                        </div>
                    )}
                </div>,
            );
        }

        return practitionersToRender;
    }, [selfSchedule?.practitionerId, practitioners, handleSetSelected]);

    if (!selfSchedule) return null;

    if (isEmptyObject(practitioners)) {
        const message = (() => {
            const locationName = selfSchedule?.location?.name
                ? ` at ${selfSchedule?.location.name}`
                : "";
            const locationPhone = selfSchedule?.location?.phoneNumber
                ? ` Please contact ${selfScheduleSettings?.accountName} at ${selfSchedule?.location?.phoneNumber} to schedule an appointment.`
                : "";

            return `Loading ${selfScheduleSettings?.practitionerFieldName}s${locationName} availability.${locationPhone}`;
        })();

        return (
            <div className="container-fluid">
                <h3 className="my-4">{`No ${selfScheduleSettings?.practitionerFieldName}s found.`}</h3>
                <p>{message}</p>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <h3 className="extra-bold mt-4">
                Select your
                <br />
                {selfScheduleSettings?.practitionerFieldName}:
            </h3>
            {render}
        </div>
    );
};

export default PatientSchedulingPractitioner;
